/**
 * Reusable chart components for commitment / exposure analysis.
 * Used by the Commitments tab in PEDashboard for both participant commitment
 * and total exposure views.
 */
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const SEGMENT_COLORS = [
  "var(--chart-fill-primary)", "var(--chart-positive)", "var(--chart-indigo)",
  "var(--chart-warning)", "var(--chart-danger)", "#8b5cf6", "#ec4899", "#14b8a6",
];

// ─── Fund-level stacked bar chart ────────────────────────────────────────────
export function FundBarChart({ data, title, chartHeight = 300, formatCurrency }) {
  if (!data || data.length === 0) {
    return <div className="h-40 flex items-center justify-center text-ink-subtle">No data available</div>;
  }

  return (
    <div>
      <h3 className="text-lg font-semibold mb-3 text-ink">{title}</h3>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
          <XAxis dataKey="name" stroke="var(--chart-axis)" style={{ fontSize: "0.65rem" }} angle={-45} textAnchor="end" height={100} />
          <YAxis stroke="var(--chart-axis)" style={{ fontSize: "0.75rem" }} />
          <Tooltip
            contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem" }}
            formatter={(value) => formatCurrency(value)}
          />
          <Legend wrapperStyle={{ paddingTop: "1rem" }} />
          <Bar dataKey="Committed" fill="var(--chart-fill-primary)" radius={[4, 4, 0, 0]} />
          <Bar dataKey="Drawn" fill="var(--chart-positive)" radius={[4, 4, 0, 0]} />
          <Bar dataKey="Remaining" fill="var(--chart-indigo)" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Vehicle-level stacked bar chart with summary cards ──────────────────────
export function VehicleBarChart({ data, title, subtitle, chartHeight = 250, isMobile = false }) {
  if (!data || data.length === 0) {
    return <div className="h-40 flex items-center justify-center text-ink-subtle">No data available</div>;
  }

  return (
    <div>
      <h3 className="text-lg font-semibold mb-3 text-ink">{title}</h3>
      {subtitle && <p className="text-ink-muted text-xs mb-3">{subtitle}</p>}
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
          <XAxis dataKey="name" tick={{ fill: "var(--chart-axis)", fontSize: 12 }} />
          <YAxis tick={{ fill: "var(--chart-axis)", fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} width={55} />
          <Tooltip
            contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "8px" }}
            formatter={(value, name) => [`$${value.toLocaleString()}`, name]}
            labelFormatter={(label) => data.find(v => v.name === label)?.fullName || label}
          />
          <Legend wrapperStyle={{ paddingTop: "0.75rem", fontSize: "12px" }} />
          <Bar dataKey="Drawn" stackId="commitment" fill="var(--chart-positive)" radius={[0, 0, 0, 0]} />
          <Bar dataKey="Unfunded" stackId="commitment" fill="var(--chart-grid)" radius={[4, 4, 0, 0]} />
          <Bar dataKey="NAV" fill="var(--chart-fill-primary)" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        {data.map((v) => {
          const total = v.Drawn + v.Unfunded;
          const pct = total > 0 ? Math.round((v.Drawn / total) * 100) : 0;
          const tvpi = v.Drawn > 0 ? (v.NAV / v.Drawn).toFixed(2) : "—";
          return (
            <div key={v.name} className="bg-surface-tertiary rounded-lg p-4 border border-edge">
              <p className="text-ink-muted text-xs mb-1">{v.fullName}</p>
              <div className="flex justify-between items-end">
                <div>
                  <p className="text-ink font-bold text-lg">{pct}% called</p>
                  <p className="text-ink-muted text-xs">${v.Drawn.toLocaleString()} of ${total.toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className={`font-semibold text-sm ${parseFloat(tvpi) >= 1 ? "text-success-fg" : "text-danger-fg"}`}>{tvpi}x NAV/Drawn</p>
                  <p className="text-ink-subtle text-xs">${v.Unfunded.toLocaleString()} remaining</p>
                </div>
              </div>
              <div className="mt-2 h-1.5 bg-surface-tertiary rounded-full overflow-hidden">
                <div className="h-full bg-success-border rounded-full" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Diversification pie charts (4 pies in a 2×2 grid) ──────────────────────
export function DiversificationPies({ fundData, valueKey = "commitment", valueLabel = "Commitment", title, description, isMobile = false }) {
  // Aggregate by classification dimensions
  const byArea = {};
  const byStrategy = {};
  const byProductType = {};
  const byCross = {};

  fundData.forEach((f) => {
    const val = f[valueKey] || 0;
    const area = f.fundArea || "Unclassified";
    const strategy = f.fundStrategy || "Unclassified";
    const pt = f.productType || "Unclassified";
    const cross = `${area} — ${pt}`;

    byArea[area] = (byArea[area] || 0) + val;
    byStrategy[strategy] = (byStrategy[strategy] || 0) + val;
    byProductType[pt] = (byProductType[pt] || 0) + val;
    byCross[cross] = (byCross[cross] || 0) + val;
  });

  const toPieData = (obj) => Object.entries(obj)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const areaData = toPieData(byArea);
  const strategyData = toPieData(byStrategy);
  const ptData = toPieData(byProductType);
  const crossData = toPieData(byCross);

  const total = fundData.reduce((s, f) => s + (f[valueKey] || 0), 0);

  const renderPie = (pieTitle, data, subtitle) => (
    <div className="bg-surface-tertiary/50 rounded-lg p-4 border border-edge-subtle">
      <h4 className="text-sm font-semibold text-ink mb-1">{pieTitle}</h4>
      {subtitle && <p className="text-xs text-ink-muted mb-3">{subtitle}</p>}
      {data.length > 0 ? (
        <ResponsiveContainer width="100%" height={isMobile ? 160 : 220}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              outerRadius={75}
              innerRadius={35}
              dataKey="value"
              nameKey="name"
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              labelLine={{ stroke: "var(--chart-axis)" }}
              style={{ fontSize: "0.65rem" }}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={SEGMENT_COLORS[i % SEGMENT_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ backgroundColor: "var(--chart-tooltip-bg)", border: "1px solid var(--chart-grid)", borderRadius: "0.5rem", fontSize: "0.75rem" }}
              formatter={(value) => [`$${Number(value).toLocaleString()}`, valueLabel]}
            />
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-[220px] flex items-center justify-center text-ink-subtle text-sm">No data</div>
      )}
      <div className="mt-2 space-y-1">
        {data.map((d, i) => (
          <div key={d.name} className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: SEGMENT_COLORS[i % SEGMENT_COLORS.length] }} />
              <span className="text-ink-muted">{d.name}</span>
            </div>
            <div className="text-ink">
              ${Number(d.value).toLocaleString()}
              <span className="text-ink-subtle ml-1">({total > 0 ? ((d.value / total) * 100).toFixed(1) : 0}%)</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <h3 className="text-lg font-semibold mb-1 text-ink">{title}</h3>
      {description && <p className="text-ink-muted text-xs mb-4">{description}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderPie("By Area", areaData, "Private Capital vs Real Assets")}
        {renderPie("By Strategy", strategyData, "Investment strategy breakdown")}
        {renderPie("By Product Type", ptData, "Core vs Specialty")}
        {renderPie("Area × Product Type", crossData, "Cross-section view")}
      </div>
    </div>
  );
}
